import { Badge, Tooltip } from "@/components/ui";

import { pickedDay } from "@/lib/clinic-clock";
import {
  formatToHumanReadable,
  toClinicDateKey,
  toUtcDateKey,
} from "@/lib/format-data";
import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";

import { MemberStatus, MembershipFrequency } from "@/types/enums/membership";

/**
 * Next occurrence on a fixed day-interval anchored to the start date.
 * Diff on UTC midnights so DST transitions don't skew the day count.
 */
function nextFixedIntervalDate(
  memberStartDate: Date,
  today: Date,
  intervalDays: number,
): Date {
  if (today < memberStartDate) {
    return memberStartDate;
  }
  const utcToday = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const utcStart = Date.UTC(
    memberStartDate.getFullYear(),
    memberStartDate.getMonth(),
    memberStartDate.getDate(),
  );
  const daysSinceStart = Math.floor(
    (utcToday - utcStart) / (24 * 60 * 60 * 1000),
  );
  const cyclesSinceStart = Math.floor(daysSinceStart / intervalDays);
  // Advance by calendar days (setDate preserves local midnight) so the
  // returned date can't drift across a DST boundary.
  const next = new Date(memberStartDate);
  next.setDate(next.getDate() + (cyclesSinceStart + 1) * intervalDays);
  return next;
}

/**
 * Calculate the next billing date for a member based on their billing frequency and start date.
 * Logic mirrors the backend implementation in membership.py
 */
export function getNextBillingDate(
  billingFrequency: MembershipFrequency,
  startDate: string,
): Date {
  const today = new Date();
  const memberStartDate = new Date(startDate);

  // Set both dates to start of day for accurate comparison
  today.setHours(0, 0, 0, 0);
  memberStartDate.setHours(0, 0, 0, 0);

  switch (billingFrequency) {
    case MembershipFrequency.DAILY:
      // Next day after today
      return new Date(today.getTime() + 24 * 60 * 60 * 1000);

    case MembershipFrequency.WEEKLY: {
      // Find next occurrence of the same day of week as start date
      const startDayOfWeek = memberStartDate.getDay();
      const todayDayOfWeek = today.getDay();
      let daysUntilNext = (startDayOfWeek - todayDayOfWeek + 7) % 7;
      if (daysUntilNext === 0) daysUntilNext = 7; // If today is the billing day, next is in 7 days
      return new Date(today.getTime() + daysUntilNext * 24 * 60 * 60 * 1000);
    }

    case MembershipFrequency.BIWEEKLY: {
      // Find next occurrence that's exactly 14 days from start date
      const daysSinceStart = Math.floor(
        (today.getTime() - memberStartDate.getTime()) / (24 * 60 * 60 * 1000),
      );
      if (daysSinceStart < 0) {
        // Start date is in the future
        return memberStartDate;
      }
      const cyclesSinceStart = Math.floor(daysSinceStart / 14);
      const nextCycleDate = new Date(
        memberStartDate.getTime() +
          (cyclesSinceStart + 1) * 14 * 24 * 60 * 60 * 1000,
      );
      return nextCycleDate;
    }

    case MembershipFrequency.MONTHLY: {
      // First day of next month
      const nextMonth = today.getMonth() === 11 ? 0 : today.getMonth() + 1;
      const nextYear =
        today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear();
      return new Date(nextYear, nextMonth, 1);
    }

    case MembershipFrequency.EVERY_FOUR_WEEKS:
      return nextFixedIntervalDate(memberStartDate, today, 28);

    case MembershipFrequency.EVERY_FIVE_WEEKS:
      return nextFixedIntervalDate(memberStartDate, today, 35);

    case MembershipFrequency.EVERY_SIX_WEEKS:
      return nextFixedIntervalDate(memberStartDate, today, 42);

    case MembershipFrequency.EVERY_TEN_WEEKS:
      return nextFixedIntervalDate(memberStartDate, today, 70);

    case MembershipFrequency.EVERY_TWELVE_WEEKS:
      return nextFixedIntervalDate(memberStartDate, today, 84);

    case MembershipFrequency.QUARTERLY: {
      // Next quarter start month among {0, 3, 6, 9} (JS months are 0-indexed)
      const quarterStarts = [0, 3, 6, 9]; // Jan, Apr, Jul, Oct
      const currentMonth = today.getMonth();
      const nextQuarterMonth =
        quarterStarts.find((month) => month > currentMonth) ?? 0;
      const quarterYear =
        nextQuarterMonth === 0 && currentMonth >= 9
          ? today.getFullYear() + 1
          : today.getFullYear();
      return new Date(quarterYear, nextQuarterMonth, 1);
    }

    case MembershipFrequency.EVERY_FOUR_MONTHS: {
      const next = new Date(memberStartDate);
      next.setHours(0, 0, 0, 0);
      if (next > today) return next;
      while (next <= today) {
        next.setMonth(next.getMonth() + 4);
      }
      return next;
    }

    case MembershipFrequency.SEMI_ANNUALLY: {
      // Next semi period start month among {0, 6} (Jan, July)
      const currentMonthSemi = today.getMonth();
      if (currentMonthSemi < 6) {
        return new Date(today.getFullYear(), 6, 1); // July 1st
      }
      return new Date(today.getFullYear() + 1, 0, 1); // Next Jan 1st
    }

    case MembershipFrequency.YEARLY:
      // Next January 1st
      return new Date(today.getFullYear() + 1, 0, 1);

    default:
      throw new Error(`Invalid billing frequency: ${billingFrequency}`);
  }
}

/**
 * Get the most recent renewal date from member logs.
 * Looks for RENEWED log type to find when the member last renewed.
 */
export function getMostRecentRenewalDate(
  logs?: Array<{ createdDate?: string; logType?: string }>,
): string | null {
  if (!logs || logs.length === 0) {
    return null;
  }

  // Filter for only RENEWED logs
  const renewalLogs = logs.filter(
    (log) => log.createdDate && log.logType === "RENEWED",
  );

  if (renewalLogs.length === 0) {
    return null;
  }

  // Sort by creation date descending and get the most recent
  const mostRecent = renewalLogs.sort(
    (a, b) =>
      new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime(),
  )[0];

  return mostRecent.createdDate!;
}

interface MemberExpiryFields {
  startDate: string;
  nextBillingDate?: string;
  endDate?: string;
  billingFrequency: MembershipFrequency;
}

/**
 * Resolve the calendar day a non-renewing member terminates: it ends at the end
 * of the current cycle rather than rolling over. Prefer a persisted end/billing
 * date, otherwise compute start + one cycle.
 *
 * Returns `YYYY-MM-DD`, because the three sources mean three different things
 * and only this function can tell them apart. Handing back whichever one
 * happened to be set would leave every caller guessing, and they guessed
 * differently.
 */
export function getMemberExpiryDate(member: MemberExpiryFields): string {
  // A cancellation stamps end_date with utcnow(), so this is a real moment —
  // the day it belongs to is the viewer's.
  if (member.endDate) return toClinicDateKey(member.endDate);

  // next_billing_date is a scheduled day living in a datetime column, written
  // at tenant-local midnight. Its day is the one it names in UTC.
  if (member.nextBillingDate) {
    const billing = toUtcDateKey(member.nextBillingDate);
    if (billing) return billing;
  }

  // Computed at browser-local midnight, so its own components are the day.
  return pickedDay(
    getNextBillingDate(member.billingFrequency, member.startDate),
  );
}

const memberStatusBadgeVariants = tv({
  base: "",
  variants: {
    status: {
      [MemberStatus.ACTIVE]: "bg-status-success-bg text-status-success-text",
      [MemberStatus.CANCELLED]: "bg-status-error-bg text-status-error-text",
      [MemberStatus.EXPIRED]: "bg-muted text-foreground",
      [MemberStatus.DELINQUENT]:
        "bg-status-warning-bg text-status-warning-text",
      [MemberStatus.FROZEN]: "bg-status-info-bg text-status-info-text",
      [MemberStatus.INACTIVE]: "bg-muted text-foreground",
      [MemberStatus.PENDING]: "bg-status-warning-bg text-status-warning-text",
      [MemberStatus.AWAITING_FORM]:
        "bg-status-warning-bg text-status-warning-text",
      [MemberStatus.SWITCHED]: "bg-muted text-foreground",
    },
  },
});

export const memberStatusDescriptions: Record<MemberStatus, string> = {
  [MemberStatus.ACTIVE]: "Membership is active and billing on schedule.",
  [MemberStatus.CANCELLED]:
    "Membership has been cancelled and will no longer renew.",
  [MemberStatus.EXPIRED]: "Membership term has ended and was not renewed.",
  [MemberStatus.DELINQUENT]:
    "A recent payment failed. Resolve the outstanding balance to restore the membership.",
  [MemberStatus.FROZEN]:
    "Membership is paused. Billing and benefits are temporarily suspended.",
  [MemberStatus.INACTIVE]:
    "Membership is no longer in effect and will not bill. Reactivate it to resume billing.",
  [MemberStatus.PENDING]:
    "Waiting for the ACH payment to clear. Membership benefits will activate once it succeeds.",
  [MemberStatus.AWAITING_FORM]:
    "Waiting for the patient to complete the enrollment form. Membership benefits will activate once it is submitted.",
  [MemberStatus.SWITCHED]:
    "Membership was switched to a different plan. Its benefits were archived and replaced by the new plan.",
};

interface MemberStatusBadgeProps {
  status: MemberStatus;
  className?: string;
}

export function MemberStatusBadge({
  status,
  className,
}: MemberStatusBadgeProps) {
  return (
    <Tooltip content={memberStatusDescriptions[status]} triggerAsChild>
      <Badge className={cx(memberStatusBadgeVariants({ status }), className)}>
        {formatToHumanReadable(status)}
      </Badge>
    </Tooltip>
  );
}
